import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { MessageCircle, Clock, Heart } from 'lucide-react';
import toast from 'react-hot-toast';

interface Comment {
  id: string;
  text: string;
  nickname: string;
  createdAt: Date;
  parentId?: string;
  likes: number;
  hasLiked?: boolean;
  replies?: Comment[];
}

interface CommentsProps {
  treeId: string;
}

const CommentItem = ({ 
  comment, 
  onReply, 
  onLike, 
  level = 0 
}: { 
  comment: Comment; 
  onReply: (parentId: string) => void;
  onLike: (commentId: string) => void;
  level?: number;
}) => {
  const [showReplies, setShowReplies] = useState(level < 2);
  const hasReplies = comment.replies && comment.replies.length > 0;

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 60) return `hace ${minutes} minutos`;
    if (hours < 24) return `hace ${hours} horas`;
    if (days === 1) return 'ayer';
    if (days < 7) return `hace ${days} días`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`space-y-4 ${level > 0 ? 'ml-8' : ''}`}>
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(comment.nickname)}&background=5D3E98&color=fff&bold=true&size=128`}
              alt={`Avatar de ${comment.nickname}`}
              className="w-10 h-10 rounded-full shadow-sm"
              loading="lazy"
            />
            <div>
              <h4 className="font-semibold text-gray-900">
                {comment.nickname}
              </h4>
              <div className="flex items-center text-sm text-gray-500">
                <Clock className="h-4 w-4 mr-1" />
                {formatDate(comment.createdAt)}
              </div>
            </div>
          </div>
          <button 
            onClick={() => onLike(comment.id)}
            className={`flex items-center space-x-1 px-3 py-1 rounded-full transition-colors ${
              comment.hasLiked 
                ? 'text-red-500 bg-red-50' 
                : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
            }`}
          >
            <Heart className="h-4 w-4" fill={comment.hasLiked ? 'currentColor' : 'none'} />
            <span className="text-sm font-medium">{comment.likes}</span>
          </button>
        </div>
        <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">
          {comment.text}
        </p>
        {/* Commenting out reply button
        <div className="mt-4 flex items-center space-x-4">
          <button
            onClick={() => onReply(comment.id)}
            className="text-primary hover:text-primary-dark text-sm flex items-center"
          >
            <CornerDownRight className="h-4 w-4 mr-1" />
            Responder
          </button>
        </div>
        */}
      </div>

      {hasReplies && (
        <div>
          <button
            onClick={() => setShowReplies(!showReplies)}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            {showReplies ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Ocultar respuestas
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Mostrar {comment.replies?.length} respuestas
              </>
            )}
          </button>

          {showReplies && (
            <div className="space-y-4">
              {comment.replies?.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  onReply={onReply}
                  onLike={onLike}
                  level={level + 1}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function Comments({ treeId }: CommentsProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  // Commenting out form state
  /*
  const [text, setText] = useState('');
  const [nickname, setNickname] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  */

  const loadComments = async () => {
    try {
      const commentsQuery = query(
        collection(db, 'comments'),
        where('treeId', '==', treeId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(commentsQuery);
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        hasLiked: localStorage.getItem(`comment_like_${doc.id}`) === 'true'
      })) as Comment[];

      // Organize comments in hierarchy
      const commentMap = new Map<string, Comment>();
      const rootComments: Comment[] = [];

      commentsData.forEach(comment => {
        commentMap.set(comment.id, { ...comment, replies: [] });
      });

      commentsData.forEach(comment => {
        if (comment.parentId) {
          const parent = commentMap.get(comment.parentId);
          if (parent) {
            parent.replies = parent.replies || [];
            parent.replies.push(commentMap.get(comment.id)!);
          }
        } else {
          rootComments.push(commentMap.get(comment.id)!);
        }
      });

      setComments(rootComments);
    } catch (error) {
      console.error('Error loading comments:', error);
      toast.error('Error al cargar los comentarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComments();
  }, [treeId]);

  const handleLike = async (commentId: string) => {
    const hasLiked = localStorage.getItem(`comment_like_${commentId}`) === 'true';
    
    try {
      const commentRef = doc(db, 'comments', commentId);
      await updateDoc(commentRef, {
        likes: increment(hasLiked ? -1 : 1)
      });

      localStorage.setItem(`comment_like_${commentId}`, (!hasLiked).toString());
      await loadComments();
    } catch (error) {
      console.error('Error updating likes:', error);
      toast.error('Error al actualizar los likes');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-20 bg-gray-100 rounded-lg"></div>
        <div className="h-20 bg-gray-100 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900 flex items-center">
          <MessageCircle className="h-6 w-6 mr-2 text-primary" />
          Comentarios ({comments.reduce((acc, comment) => acc + 1 + (comment.replies?.length || 0), 0)})
        </h3>
        {/* Commenting out new comment button
        {!showForm && (
          <button
            onClick={() => {
              setShowForm(true);
              setReplyingTo(null);
            }}
            className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded-lg flex items-center transition-colors"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Escribir comentario
          </button>
        )}
        */}
      </div>

      {/* Commenting out comment form
      {showForm && (
        <div id="comment-form" className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
          {replyingTo && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 flex items-center">
              <CornerDownRight className="h-4 w-4 mr-2" />
              Respondiendo a un comentario
              <button
                onClick={() => setReplyingTo(null)}
                className="ml-auto text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center space-x-4 mb-4">
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(nickname || 'Anónimo')}&background=5D3E98&color=fff&bold=true&size=128`}
                alt="Avatar"
                className="w-10 h-10 rounded-full"
              />
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Tu nombre (opcional)"
                maxLength={30}
                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-50"
              />
            </div>
            
            <div className="relative">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Escribe tu comentario..."
                maxLength={500}
                rows={3}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-50 resize-none"
                required
              />
              <span className="absolute bottom-2 right-2 text-xs text-gray-400">
                {text.length}/500
              </span>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setReplyingTo(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !text.trim()}
                className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded-lg flex items-center disabled:opacity-50 transition-colors"
              >
                <Send className="h-4 w-4 mr-2" />
                {isSubmitting ? 'Enviando...' : 'Publicar comentario'}
              </button>
            </div>
          </form>
        </div>
      )}
      */}

      <div className="space-y-6">
        {comments.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            onReply={() => {}} // Empty function since replies are disabled
            onLike={handleLike}
          />
        ))}

        {comments.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
            <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay comentarios aún</p>
          </div>
        )}
      </div>
    </div>
  );
}